import { useState, useEffect, useMemo, useCallback } from "react";
import api, { money, coladoTotal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { HardHat, Plus, CalendarPlus, FileDown, LogOut, UserPlus, Trash2, ChevronDown, Loader2 } from "lucide-react";
import BacklogPanel from "@/components/BacklogPanel";
import ColadorColumn from "@/components/ColadorColumn";
import ColadoDialog from "@/components/ColadoDialog";
import LiquidationDialog from "@/components/LiquidationDialog";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [weeks, setWeeks] = useState([]);
  const [activeWeek, setActiveWeek] = useState("");
  const [coladores, setColadores] = useState([]);
  const [colados, setColados] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // dialogs
  const [weekOpen, setWeekOpen] = useState(false);
  const [weekForm, setWeekForm] = useState({ label: "", start_date: "", end_date: "", rollover: true });
  const [coladorOpen, setColadorOpen] = useState(false);
  const [coladorForm, setColadorForm] = useState({ id: null, name: "", phone: "", notes: "" });
  const [coladoOpen, setColadoOpen] = useState(false);
  const [coladoEditing, setColadoEditing] = useState(null);
  const [coladoMode, setColadoMode] = useState("backlog");
  const [liqOpen, setLiqOpen] = useState(false);
  const [liqCtx, setLiqCtx] = useState({ colado: null, colador: null, suggested: null });
  const [confirm, setConfirm] = useState(null); // {title, desc, action}
  const [downloading, setDownloading] = useState(false);

  const loadBase = useCallback(async () => {
    const [w, c] = await Promise.all([api.get("/weeks"), api.get("/coladores")]);
    setWeeks(w.data);
    setColadores(c.data);
    if (w.data.length && !activeWeek) setActiveWeek(w.data[0].id);
    if (!w.data.length) setLoading(false);
  }, [activeWeek]);

  const loadWeekData = useCallback(async (wid) => {
    if (!wid) return;
    setLoading(true);
    const [cl, pm] = await Promise.all([
      api.get(`/colados?week_id=${wid}`),
      api.get(`/payments?week_id=${wid}`),
    ]);
    setColados(cl.data);
    setPayments(pm.data);
    setLoading(false);
  }, []);

  useEffect(() => { loadBase(); }, []); // eslint-disable-line
  useEffect(() => { if (activeWeek) loadWeekData(activeWeek); }, [activeWeek, loadWeekData]);

  const backlog = useMemo(() => colados.filter((c) => !c.colador_id), [colados]);
  const totalPendiente = useMemo(() => {
    const trabajo = colados.filter((c) => c.colador_id).reduce((s, c) => s + coladoTotal(c), 0);
    const pagos = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    return trabajo - pagos;
  }, [colados, payments]);

  const currentWeek = weeks.find((w) => w.id === activeWeek);

  // ---- Weeks ----
  const createWeek = async () => {
    if (!weekForm.label.trim()) return toast.error("Ponle un nombre a la semana");
    const { data } = await api.post("/weeks", weekForm);
    setWeekOpen(false);
    setWeekForm({ label: "", start_date: "", end_date: "", rollover: true });
    const w = await api.get("/weeks");
    setWeeks(w.data);
    setActiveWeek(data.id);
    toast.success("Semana creada");
  };

  const deleteWeek = async (wid) => {
    await api.delete(`/weeks/${wid}`);
    const w = await api.get("/weeks");
    setWeeks(w.data);
    setActiveWeek(w.data[0]?.id || "");
    if (!w.data.length) { setColados([]); setPayments([]); }
    toast.success("Semana eliminada");
  };

  // ---- Coladores ----
  const saveColador = async () => {
    if (!coladorForm.name.trim()) return toast.error("Nombre requerido");
    if (coladorForm.id) await api.put(`/coladores/${coladorForm.id}`, coladorForm);
    else await api.post("/coladores", coladorForm);
    setColadorOpen(false);
    setColadorForm({ id: null, name: "", phone: "", notes: "" });
    const c = await api.get("/coladores");
    setColadores(c.data);
    toast.success("Colador guardado");
  };

  const deleteColador = (colador) => setConfirm({
    title: `Eliminar a ${colador.name}`,
    desc: "Sus colados regresarán a la agenda semanal. Esta acción no se puede deshacer.",
    action: async () => {
      await api.delete(`/coladores/${colador.id}`);
      const c = await api.get("/coladores");
      setColadores(c.data);
      loadWeekData(activeWeek);
      toast.success("Colador eliminado");
    },
  });

  // ---- Colados ----
  const openNewColado = () => { setColadoEditing(null); setColadoMode("backlog"); setColadoOpen(true); };
  const openEditColado = (c) => { setColadoEditing(c); setColadoMode(c.colador_id ? "assign" : "backlog"); setColadoOpen(true); };

  const saveColado = async (payload) => {
    if (coladoEditing?.id) {
      await api.put(`/colados/${coladoEditing.id}`, payload);
      toast.success("Colado actualizado");
    } else {
      await api.post("/colados", { week_id: activeWeek, ...payload });
      toast.success("Colado agregado a la agenda");
    }
    setColadoOpen(false);
    loadWeekData(activeWeek);
  };

  const assignColado = async (colado, coladorId, day) => {
    await api.put(`/colados/${colado.id}`, { colador_id: coladorId, day });
    loadWeekData(activeWeek);
    toast.success("Colado asignado");
  };

  const unassignColado = async (c) => {
    await api.put(`/colados/${c.id}`, { unassign: true });
    loadWeekData(activeWeek);
    toast.success("Regresado a la agenda");
  };

  const deleteColado = (c) => setConfirm({
    title: "Eliminar colado", desc: "Se eliminará permanentemente.",
    action: async () => { await api.delete(`/colados/${c.id}`); loadWeekData(activeWeek); toast.success("Colado eliminado"); },
  });

  // ---- Payments ----
  const openLiquidate = (colado, colador, suggested) => { setLiqCtx({ colado, colador, suggested }); setLiqOpen(true); };
  const openAdvance = (colador) => { setLiqCtx({ colado: null, colador, suggested: null }); setLiqOpen(true); };

  const savePayment = async (payload) => {
    await api.post("/payments", { week_id: activeWeek, colador_id: liqCtx.colador.id, ...payload });
    setLiqOpen(false);
    loadWeekData(activeWeek);
    toast.success("Pago registrado");
  };

  const deletePayment = (p) => setConfirm({
    title: "Eliminar pago", desc: "El colado volverá a pendiente si aplica.",
    action: async () => { await api.delete(`/payments/${p.id}`); loadWeekData(activeWeek); toast.success("Pago eliminado"); },
  });

  // ---- Reports ----
  const download = async (colador = null) => {
    if (!activeWeek) return;
    setDownloading(true);
    try {
      const url = `/reports/week/${activeWeek}${colador ? `?colador_id=${colador.id}` : ""}`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `reporte${colador ? "-" + colador.name : "-general"}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Reporte generado");
    } catch (e) {
      toast.error("No se pudo generar el reporte");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="px-4 sm:px-6 h-16 flex items-center gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-md bg-orange-600 flex items-center justify-center">
              <HardHat className="h-5 w-5 text-white" />
            </div>
            <span className="font-display font-bold text-slate-900 hidden sm:block tracking-tight">Control de Coladores</span>
          </div>

          <div className="flex items-center gap-2 ml-2">
            <Select value={activeWeek} onValueChange={setActiveWeek}>
              <SelectTrigger className="w-[190px] h-9" data-testid="week-selector">
                <SelectValue placeholder="Selecciona semana" />
              </SelectTrigger>
              <SelectContent>
                {weeks.map((w) => <SelectItem key={w.id} value={w.id}>{w.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {currentWeek && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><button className="p-1.5 text-slate-400 hover:text-red-500" data-testid="week-menu"><ChevronDown className="h-4 w-4" /></button></DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white">
                  <DropdownMenuItem className="text-red-600" onClick={() => setConfirm({ title: "Eliminar semana", desc: "Se archivará la semana y sus datos.", action: () => deleteWeek(activeWeek) })} data-testid="delete-week-btn">
                    <Trash2 className="h-4 w-4 mr-2" />Eliminar semana
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <Button onClick={() => setWeekOpen(true)} variant="outline" className="h-9 border-slate-300" data-testid="new-week-btn">
            <CalendarPlus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Nueva semana</span>
          </Button>

          <div className="flex-1" />

          {activeWeek && (
            <Button onClick={() => download(null)} disabled={downloading} className="h-9 bg-slate-900 hover:bg-slate-800" data-testid="download-general-btn">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 sm:mr-1.5" />}<span className="hidden sm:inline">Reporte general</span>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center text-sm font-semibold text-slate-700" data-testid="user-menu">
                {(user?.name || "A")[0]}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white">
              <div className="px-2 py-1.5 text-xs text-slate-500">{user?.email}</div>
              <DropdownMenuItem onClick={logout} data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" />Cerrar sesión</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {activeWeek && (
          <div className="px-4 sm:px-6 py-2.5 bg-slate-900 text-white flex items-center justify-between">
            <span className="text-sm text-slate-300">Total pendiente por pagar esta semana</span>
            <span className="font-display font-bold text-xl" data-testid="total-pendiente">{money(totalPendiente)}</span>
          </div>
        )}
      </header>

      {/* Body */}
      {!weeks.length ? (
        <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
          <div className="h-16 w-16 rounded-lg bg-slate-100 flex items-center justify-center mb-4"><CalendarPlus className="h-8 w-8 text-slate-400" /></div>
          <h2 className="font-display text-xl font-bold text-slate-900">Empieza creando tu primera semana</h2>
          <p className="text-slate-500 mt-1 mb-5 max-w-sm">Organiza los colados, cuadrillas y pagos por semana.</p>
          <Button onClick={() => setWeekOpen(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="empty-new-week-btn"><CalendarPlus className="h-4 w-4 mr-1.5" />Crear semana</Button>
        </div>
      ) : (
        <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 lg:gap-6 items-start">
          <div className="lg:h-[calc(100vh-9.5rem)] lg:sticky lg:top-[7rem]">
            <BacklogPanel backlog={backlog} coladores={coladores}
              onAdd={openNewColado} onAssign={assignColado} onEdit={openEditColado}
              onDelete={deleteColado} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold text-lg text-slate-900">Coladores <span className="text-slate-400 text-base font-normal">({coladores.length})</span></h2>
              <Button onClick={() => { setColadorForm({ id: null, name: "", phone: "", notes: "" }); setColadorOpen(true); }} className="bg-orange-600 hover:bg-orange-700 h-9" data-testid="new-colador-btn">
                <UserPlus className="h-4 w-4 mr-1.5" />Nuevo colador
              </Button>
            </div>

            {coladores.length === 0 ? (
              <div className="bg-white rounded-lg border border-dashed border-slate-300 py-16 text-center">
                <p className="text-slate-500">Agrega tu primer colador para asignarle trabajos.</p>
              </div>
            ) : (
              <div className="flex gap-4 overflow-x-auto custom-scroll pb-4 items-start">
                {coladores.map((cl) => (
                  <ColadorColumn key={cl.id} colador={cl}
                    colados={colados.filter((c) => c.colador_id === cl.id)}
                    payments={payments.filter((p) => p.colador_id === cl.id)}
                    onAddPayment={openAdvance} onLiquidate={openLiquidate}
                    onEditColado={openEditColado} onDeleteColado={deleteColado}
                    onUnassign={unassignColado} onDownload={download}
                    onDeleteColador={deleteColador}
                    onEditColador={(c) => { setColadorForm({ id: c.id, name: c.name, phone: c.phone || "", notes: c.notes || "" }); setColadorOpen(true); }}
                    onDeletePayment={deletePayment} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Week dialog */}
      <Dialog open={weekOpen} onOpenChange={setWeekOpen}>
        <DialogContent className="max-w-md" data-testid="week-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Nueva semana</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre de la semana</Label>
              <Input value={weekForm.label} onChange={(e) => setWeekForm({ ...weekForm, label: e.target.value })} placeholder="Ej. Semana 23 (2 - 7 Jun)" className="mt-1.5" data-testid="week-label-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Inicio</Label><Input type="date" value={weekForm.start_date} onChange={(e) => setWeekForm({ ...weekForm, start_date: e.target.value })} className="mt-1.5" data-testid="week-start-input" /></div>
              <div><Label>Fin</Label><Input type="date" value={weekForm.end_date} onChange={(e) => setWeekForm({ ...weekForm, end_date: e.target.value })} className="mt-1.5" data-testid="week-end-input" /></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={weekForm.rollover} onChange={(e) => setWeekForm({ ...weekForm, rollover: e.target.checked })} className="rounded border-slate-300" data-testid="week-rollover-check" />
              Pasar colados pendientes de la agenda anterior
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWeekOpen(false)}>Cancelar</Button>
            <Button onClick={createWeek} className="bg-orange-600 hover:bg-orange-700" data-testid="week-create-btn">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Colador dialog */}
      <Dialog open={coladorOpen} onOpenChange={setColadorOpen}>
        <DialogContent className="max-w-md" data-testid="colador-dialog">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{coladorForm.id ? "Editar colador" : "Nuevo colador"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre / cuadrilla</Label><Input value={coladorForm.name} onChange={(e) => setColadorForm({ ...coladorForm, name: e.target.value })} placeholder="Ej. Cuadrilla Juan" className="mt-1.5" data-testid="colador-name-input" /></div>
            <div><Label>Teléfono</Label><Input value={coladorForm.phone} onChange={(e) => setColadorForm({ ...coladorForm, phone: e.target.value })} placeholder="Opcional" className="mt-1.5" data-testid="colador-phone-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setColadorOpen(false)}>Cancelar</Button>
            <Button onClick={saveColador} className="bg-orange-600 hover:bg-orange-700" data-testid="colador-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ColadoDialog open={coladoOpen} onOpenChange={setColadoOpen} onSave={saveColado}
        initial={coladoEditing} coladores={coladores} mode={coladoMode} />

      <LiquidationDialog open={liqOpen} onOpenChange={setLiqOpen} onSave={savePayment}
        colado={liqCtx.colado} colador={liqCtx.colador} suggested={liqCtx.suggested} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={async () => { const a = confirm?.action; setConfirm(null); if (a) await a(); }} data-testid="confirm-accept">Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
